import { createElement, useLayoutEffect, useRef, type CSSProperties, type ReactNode } from "react";

/* ------------------------------------------------------------------ *
 * Per-seat card entry motion — ONE helper for all five tables.
 *
 * THE BUG THIS EXISTS TO FIX: every card played into a trick/pool/pile flew in
 * from the BOTTOM of the screen, whoever played it. The tables asked the shared
 * `Card` for its `thrown` entrance, and that entrance is the `card-throw`
 * keyframe (styles/theme.css), whose 0% frame is a hardcoded
 * `translateY(220px)` — i.e. "up from below the viewer". Correct for your own
 * card, wrong for the other three seats: the partner's card climbed UP past the
 * felt centre instead of dropping DOWN from their seat.
 *
 * THE FIX: the card flies in from ITS OWN player's edge. The direction can't
 * live in a static keyframe (it differs per card, per render), so the entrance
 * moves to WAAPI — the same mechanism the trick collect-sweep already uses. A
 * runtime without `Element.animate` (happy-dom / SSR) simply renders the card at
 * rest, exactly as the sweep degrades.
 *
 * NOTHING ELSE MOVES: the trick HOLD → sweep → hide machine, its timings, and
 * the sound cues that ride them (`use-table-sfx`) are untouched — this only
 * changes where a card's ~400ms entrance STARTS.
 * ------------------------------------------------------------------ */

/** The felt edge a card flies in from — the four seat positions RELATIVE TO THE
 *  VIEWER (physical directions, so they are RTL-independent, like `collectSweep`). */
export type SeatEdge = "bottom" | "right" | "top" | "left";

/** The translate that sweeps a collected trick toward the WINNER's edge, relative to you: you →
 *  down, right opponent → right, across → up, left opponent → left. Physical, so RTL-independent.
 *  The trick hold's collect (`use-trick-hold.ts`) sweeps with it; a capture toward a relative seat
 *  (پاسور's) is the same sweep. */
export function collectSweep(rel: number): string {
  const map: Record<number, string> = {
    0: "translate(0px, 150px)",
    1: "translate(150px, 0px)",
    2: "translate(0px, -150px)",
    3: "translate(-150px, 0px)",
  };
  return map[rel] ?? map[0]!;
}

/** How far outside its resting slot a card starts — the same magnitude the old
 *  bottom-only `card-throw` used (`translateY(220px)`), so your OWN card's
 *  entrance is pixel-for-pixel what it has always been. */
const ENTRY_DISTANCE_PX = 220;

/** Entry duration + easing — `card-throw`'s (`.4s cubic-bezier(.2,.7,.3,1)`) verbatim,
 *  so no table's pacing shifts. Both stay well under every trick hold (≥600ms). */
export const CARD_ENTRY_MS = 400;
export const CARD_ENTRY_EASING = "cubic-bezier(.2,.7,.3,1)";

/** The starting offset (px, screen axes: +x right, +y down) for a card thrown from
 *  `edge` — i.e. push it OUT toward the seat that played it, then fly it back in. */
export function cardEntryOffset(edge: SeatEdge): { x: number; y: number } {
  const d = ENTRY_DISTANCE_PX;
  switch (edge) {
    case "bottom":
      return { x: 0, y: d };
    case "top":
      return { x: 0, y: -d };
    case "left":
      return { x: -d, y: 0 };
    case "right":
      return { x: d, y: 0 };
  }
}

/** The small flick-tilt a card carries out of its player's hand; it settles to
 *  rotate(0) so the wrapper's own resting tilt (Shelem/Pasur/Bluff) is never
 *  clobbered — exactly what `card-throw` did. */
function entryTilt(edge: SeatEdge): number {
  return edge === "right" || edge === "top" ? 6 : -6;
}

/** The CSS transform a card thrown from `edge` STARTS at (its 0% frame). Pure —
 *  this is the seat-derived value the tables' motion is built from. */
export function cardEntryTransform(edge: SeatEdge): string {
  const { x, y } = cardEntryOffset(edge);
  return `translate(${x}px, ${y}px) scale(0.8) rotate(${entryTilt(edge)}deg)`;
}

/** The card's flight: out at its player's seat and invisible → in, upright, at rest.
 *  Mirrors `@keyframes card-throw` (incl. its 60% opacity stop) with the START
 *  point rotated to the player's edge instead of always being "below the viewer". */
export function cardEntryKeyframes(edge: SeatEdge): Keyframe[] {
  return [
    { transform: cardEntryTransform(edge), opacity: 0 },
    { opacity: 1, offset: 0.6 },
    { transform: "none", opacity: 1 },
  ];
}

/**
 * The four-seat DIAMOND layout — Hokm, Bidel and Shelem, and Pasur since P1 Task 3 (its partner
 * across). Relative seat (`(seat - you + n) % n`)
 * 0 is YOU at the bottom, 1 the RIGHT opponent, 2 the seat opposite you, 3 the LEFT
 * opponent. Verified against Hokm's and Bidel's own `slotFor` maps and their shared `collectSweep`
 * (above) — rel 1 → `left: calc(50% + 64px)` and sweeps `translate(+150px, 0)` = right.
 * Shelem drew its flanks the other way round off a map of its own until P0-7 (2026-10-09:
 * «every card game here turns counter-clockwise like حکم»); its `TRICK_SLOT` and `<Seat>`s
 * now put rel 1 on the right too.
 */
export const DIAMOND_SEAT_EDGES = [
  "bottom",
  "right",
  "top",
  "left",
] as const satisfies readonly SeatEdge[];

/** Look a relative seat up in a layout map (out-of-range → the viewer's own edge). */
export function seatEdge(edges: readonly SeatEdge[], rel: number): SeatEdge {
  return edges[rel] ?? "bottom";
}

/**
 * Where پاسور's table draws a seat, relative to you — and the edge its card flies in from: حکم's
 * diamond at four seats ({@link DIAMOND_SEAT_EDGES}: seat+1 on your RIGHT, your partner across, seat+3
 * on your LEFT); at TWO seats the one opponent ACROSS, as on every table for two. ONE rule for the
 * table (`PasurTable`) and the waiting room (`TABLE_SEAT_LAYOUT.pasur`), which since the tables for
 * two (operator, 2026-10-20) shows that size too — «nobody moves between the waiting room and the
 * deal» (`waiting-room-seat-sides.test.tsx`).
 */
export function pasurSeatEdge(seat: number, you: number, seats: number): SeatEdge {
  const rel = (((seat - you) % seats) + seats) % seats;
  if (seats === 2) return rel === 0 ? "bottom" : "top";
  return seatEdge(DIAMOND_SEAT_EDGES, rel);
}

/**
 * Bluff doesn't seat players around a felt: it lines EVERY opponent up in a ROW ALONG
 * THE TOP (2–6 of them) with you alone at the bottom. So an opponent's card drops in
 * from the top whoever they are, and yours flies up out of your own hand. (Pasur did
 * too until P1 Task 3 put it on the diamond.)
 */
export function rowSeatEdge(seat: number, you: number): SeatEdge {
  return seat === you ? "bottom" : "top";
}

/** Honour the OS "reduce motion" setting — no flight at all, the card just appears.
 *  Guarded for non-browser runtimes (SSR / a test env without matchMedia). */
export function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * A card that flies in from the seat that played it.
 *
 * Wrap the `<Card>` in this (NOT `<Card thrown />`, which is the bottom-only
 * entrance) and KEY it by the card, so a freshly-played card is a fresh mount and
 * a card carried from `currentTrick` into `completedTrick` keeps its node and does
 * NOT re-fly (the invariant every trick layer already relies on).
 *
 * The wrapper must be — and here always is — an element with NO resting transform
 * of its own: the WAAPI animation drives `transform`, so a static one on the same
 * element would be overridden. Every caller therefore nests this INSIDE its slot /
 * tilt / fan wrapper, which keeps owning the resting position (the same layering
 * `card-throw` needed, e.g. Bluff's "rides an inner wrapper so it doesn't fight the
 * fan rotate").
 *
 * `data-enter-from` records which edge the card flew from — the entrance's one
 * stable, observable marker (absent when motion is off).
 */
export function ThrownCard({
  edge,
  children,
  className,
  style,
}: {
  /** The edge to fly in from, or `null` to place the card with no motion at all. */
  edge: SeatEdge | null;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}): ReactNode {
  const ref = useRef<HTMLSpanElement | null>(null);
  // Read the preference ONCE per mount: the entrance is a one-shot, so there is
  // nothing to re-evaluate if the setting changes mid-flight.
  const flying = edge !== null && !prefersReducedMotion();

  useLayoutEffect(() => {
    if (!flying) return undefined;
    const el = ref.current;
    // WAAPI-only, like the trick collect-sweep: without `.animate` (happy-dom / SSR)
    // the card simply renders at rest — no motion, nothing broken.
    if (!el || typeof el.animate !== "function") return undefined;
    const anim = el.animate(cardEntryKeyframes(edge), {
      duration: CARD_ENTRY_MS,
      easing: CARD_ENTRY_EASING,
      // `both` so the 0% frame (card OUT at its player's seat) applies from the very
      // first paint — this runs in a LAYOUT effect, before the browser paints, so the
      // card is never glimpsed at its resting slot first.
      fill: "both",
    });
    // …and drop that fill the instant it lands. A FINISHED transform animation that
    // keeps filling makes its element a containing block for `position:fixed`
    // descendants forever (the `view-enter` trap that trapped the nav drawer). The
    // final frame is `transform:none; opacity:1` — identical to the base style — so
    // cancelling is visually seamless.
    //
    // `onfinish` (the Animation PROPERTY), never `addEventListener`: the animation is
    // the only thing we hold, and a property assignment is total — it cannot throw on a
    // partial Animation (the tables' own tests stub `.animate` as `{ cancel }`, and an
    // `addEventListener` call on that stub threw inside this layout effect and took the
    // whole render down). Same reason `cancel` is the only method we call.
    anim.onfinish = (): void => anim.cancel();
    return () => anim.cancel();
    // MOUNT-ONCE, deliberately empty deps: a card's entrance plays exactly once, when
    // its node appears. `edge`/`flying` are fixed for the life of that node (callers key
    // this wrapper by the card), so there is nothing to re-run on — and re-running would
    // re-fly a settled card on every unrelated re-render (a turn-timer tick, a score
    // bump). This is the same contract `<Card thrown>`'s mount-animation had.
  }, []);

  return createElement(
    "span",
    {
      ref,
      className,
      style: { display: "inline-flex", ...style },
      ...(flying ? { "data-enter-from": edge } : {}),
    },
    children,
  );
}
